import { recordActivity } from '../../../services/engagement';
import React, { useState, useMemo } from "react";
import { TbCopy, TbTrash, TbRefresh, TbBan, TbX } from "react-icons/tb";
import { useScanLinks } from "../../hooks/useScanLinks";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useEmailTemplates } from "../../hooks/useProspecting";
import { fmtRelative } from "../../lib/dates";
import { statusFor } from "../../lib/status";
import { ALL_GROUP_KEYS } from "../../lib/modules";
import { Button, Field, Alert, EmptyState, SkeletonRows, ConfirmModal, Toggle, SettingRow } from "../../components";
import "./Scanner.css";

// Generate scan link: the list is the screen, and minting a new one happens in a side
// panel rather than a form stacked above the table. That way the existing links stay
// visible while you create another, which is the common case.
//
// Worth knowing while reading the status column: the backend only ever writes
// 'created' or 'revoked'. Nothing sets 'opened' or 'used', and `used_at` is read by the
// table but written by no route. The Expired view is derived from expires_at rather
// than from a stored status, because there is no stored status for it.

const VIEWS = [
  { key: "all",     label: "All" },
  { key: "active",  label: "Active" },
  { key: "expired", label: "Expired" },
  { key: "revoked", label: "Revoked" },
];

const isExpired = (l) => l.expires_at && new Date(l.expires_at).getTime() < Date.now();

const GenerateScanLinkScreen = () => {
  const { links, loading, error, busy, create, revoke, regenerate, remove } = useScanLinks();
  const { organizations } = useOrganizations();
  const { templates } = useEmailTemplates("uncredential");

  const [view, setView] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const [copied, setCopied] = useState(null);

  // New-link panel
  const [domain, setDomain] = useState("");
  const [orgId, setOrgId] = useState("");
  const [instantStart, setInstantStart] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [draftEmail, setDraftEmail] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState("");
  const [minted, setMinted] = useState("");

  if (!orgId && organizations.length) setTimeout(() => setOrgId(String(organizations[0].id)), 0);

  const counts = useMemo(() => ({
    all: links.length,
    active: links.filter((l) => l.status !== "revoked" && !isExpired(l)).length,
    expired: links.filter((l) => isExpired(l)).length,
    revoked: links.filter((l) => l.status === "revoked").length,
  }), [links]);

  const rows = useMemo(() => {
    if (view === "active")  return links.filter((l) => l.status !== "revoked" && !isExpired(l));
    if (view === "expired") return links.filter((l) => isExpired(l));
    if (view === "revoked") return links.filter((l) => l.status === "revoked");
    return links;
  }, [links, view]);

  const copy = async (value, id) => {
    try { await navigator.clipboard.writeText(value); recordActivity('scan_links.copied'); setCopied(id); setTimeout(() => setCopied(null), 1800); }
    catch { /* clipboard blocked; the value is on screen to copy by hand */ }
  };

  const addRecipient = () => {
    const v = draftEmail.trim();
    if (!v || recipients.includes(v)) return;
    setRecipients((r) => [...r, v]);
    setDraftEmail("");
  };

  const mint = async () => {
    setMinting(true); setMintError(""); setMinted("");
    try {
      const link = await create({ domain: domain.trim(), groups: ALL_GROUP_KEYS, organizationId: orgId, instantStart });
      setMinted(link || "");
      setDomain(""); setRecipients([]);
    } catch (e) { setMintError(e.message || "Could not create the link."); }
    finally { setMinting(false); }
  };

  return (
    <div className="v2-fullscreen">
      <div className="v2-views">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" className={`v2-view${view === v.key ? " on" : ""}`} onClick={() => setView(v.key)}>
            {v.label}<span className="n">{counts[v.key]}</span>
          </button>
        ))}
      </div>

      <div className="v2-panes">
        <div className="v2-panes-main">
          <Alert>{error}</Alert>
          <div className="v2-tablewrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Target</th><th className="v2-num">Modules</th><th>Status</th>
                  <th>Link</th><th>Expires</th><th className="v2-num">Runs</th><th className="v2-num">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <SkeletonRows rows={5} cols={7} />
                  : rows.length === 0 ? (
                    <tr><td colSpan={7}><EmptyState title={view === "all" ? "No scan links yet" : `No ${view} links`}>
                      {view === "all" ? "Create one from the panel on the right." : "Try another view."}
                    </EmptyState></td></tr>
                  ) : rows.map((l) => {
                    const s = statusFor(isExpired(l) && l.status !== "revoked" ? "created" : l.status);
                    const rowBusy = busy?.id === l.id;
                    return (
                      <tr key={l.id}>
                        <td className="v2-mono v2-trunc" title={l.target}>{l.target}</td>
                        <td className="v2-num v2-dim">{(l.modules || []).length || "—"}</td>
                        <td><span className={`v2-status ${s.tone}`}><span className="v2-status-dot" />{s.label}</span></td>
                        <td className="v2-mono v2-dim v2-trunc" title={l.link}>{l.link}</td>
                        <td className="v2-dim">{isExpired(l) ? "Expired" : l.expires_at ? fmtRelative(l.expires_at) : "—"}</td>
                        <td className="v2-num v2-dim">{l.run_count ?? 0} of {l.max_runs ?? 1}</td>
                        <td className="v2-num">
                          <Button icon title={copied === l.id ? "Copied" : "Copy link"} onClick={() => copy(l.link, l.id)}><TbCopy size={15} /></Button>
                          <Button icon title="Regenerate" disabled={rowBusy} onClick={() => regenerate(l.id)}><TbRefresh size={15} /></Button>
                          <Button icon title="Revoke" disabled={rowBusy || l.status === "revoked"} onClick={() => revoke(l.id)}><TbBan size={15} /></Button>
                          <Button icon variant="danger" title="Delete" disabled={rowBusy} onClick={() => setConfirm(l)}><TbTrash size={15} /></Button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="v2-pane-side">
          <div>
            <div className="v2-section-title" style={{ marginBottom: ".5rem" }}>New link</div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".625rem" }}>
              <Field label="Domain or public IP addresses" hint="Enter one domain, or comma-separated public IPv4 addresses, ranges or CIDRs. Up to 256 unique IPs.">
                <textarea className="v2-input" rows={3} maxLength={8192} aria-label="Domain or public IP addresses"
                          value={domain} onChange={(e) => setDomain(e.target.value)}
                          placeholder="example.com or 8.8.8.8, 9.9.9.0/30" />
              </Field>
              <Field label="Organization" hint="Automatic IP discovery uses the saved organization's registered name, website and managed inventory. Choose the organization that owns the target domain. A registered company name is optional.">
                <select className="v2-input" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                  {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Expires in">
                {/* Not a choice: the client fixes this at seven days. */}
                <input className="v2-input" value="7 days (fixed)" disabled readOnly />
              </Field>
            </div>
          </div>

          <div>
            <SettingRow name="Start scan on open" desc="Runs immediately instead of waiting for a click">
              <Toggle checked={instantStart} onChange={setInstantStart} ariaLabel="Start scan on open" />
            </SettingRow>
          </div>

          <div>
            <div className="v2-section-title" style={{ marginBottom: ".5rem" }}>Send to</div>
            <div className="v2-recipients">
              {recipients.map((r) => (
                <span key={r} className="v2-chip">
                  {r}
                  <button type="button" className="v2-chip-x" aria-label={`Remove ${r}`} onClick={() => setRecipients((x) => x.filter((y) => y !== r))}>
                    <TbX size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="v2-addrow">
              <input className="v2-input" type="email" value={draftEmail} placeholder="name@example.com"
                     onChange={(e) => setDraftEmail(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }} />
              <Button onClick={addRecipient} disabled={!draftEmail.trim()}>Add</Button>
            </div>

            <Field label="Email template">
              <select className="v2-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">No template</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>

            <Alert>{mintError}</Alert>
            {minted && (
              <div className="v2-minted" style={{ marginTop: ".625rem" }}>
                <span className="v2-mono">{minted}</span>
                <Button onClick={() => copy(minted, "minted")}>{copied === "minted" ? "Copied" : "Copy"}</Button>
              </div>
            )}

            <Button variant="primary" style={{ width: "100%", marginTop: ".625rem" }}
                    disabled={!domain.trim() || !orgId || minting} onClick={mint}>
              {minting ? "Creating…" : recipients.length ? "Create and send" : "Create link"}
            </Button>
            {recipients.length === 0 && (
              <p className="v2-hint" style={{ marginTop: ".375rem" }}>Add a recipient to email the link as well as creating it.</p>
            )}
          </div>
        </aside>
      </div>

      {confirm && (
        <ConfirmModal title="Delete this scan link?"
          body={`The link for ${confirm.target} will be removed permanently. Revoking instead keeps the record.`}
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm.id); setConfirm(null); }} />
      )}
    </div>
  );
};

export default GenerateScanLinkScreen;
